import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ActivityViewProps } from '../../core/types/activity.types';
import { GAME_MODE_CATALOG } from '../../core/types/gameMode.types';
import type { PinpointHeatCell } from '../../core/types/payloads/pinpoint.types';



const ACCENT = '#0ea5e9';

const MIN_ZOOM = 1;

const MAX_ZOOM = 4;



interface ImageLayout {

  left: number;

  top: number;

  width: number;

  height: number;

}



function computeContainLayout(

  containerW: number,

  containerH: number,

  imgW: number,

  imgH: number,

): ImageLayout {

  if (!containerW || !containerH || !imgW || !imgH) {

    return { left: 0, top: 0, width: containerW, height: containerH };

  }

  const scale = Math.min(containerW / imgW, containerH / imgH);

  const width = imgW * scale;

  const height = imgH * scale;

  return {

    left: (containerW - width) / 2,

    top: (containerH - height) / 2,

    width,

    height,

  };

}



function Shell({

  badge,

  title,

  prompt,

  children,

  fill,

}: {

  badge?: string;

  title: string;

  prompt: string;

  children: React.ReactNode;

  fill?: boolean;

}) {

  if (fill) {

    return (

      <div className="flex h-full min-h-0 flex-col" style={{ color: 'var(--plugin-text)' }}>

        <header className="shrink-0 pb-3">

          {badge && (

            <span

              className="mb-2 inline-block rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest"

              style={{ background: `${ACCENT}22`, color: ACCENT }}

            >

              {badge}

            </span>

          )}

          <h3 className="font-display text-lg font-bold sm:text-xl">{title}</h3>

          <p className="mt-1 text-sm sm:text-base" style={{ color: 'var(--plugin-muted)' }}>

            {prompt}

          </p>

        </header>

        <div className="relative min-h-0 flex-1">{children}</div>

      </div>

    );

  }



  return (

    <div

      className="rounded-2xl p-5 sm:p-8"

      style={{ background: 'var(--plugin-bg)', border: '1px solid var(--plugin-border)', color: 'var(--plugin-text)' }}

    >

      {badge && (

        <span

          className="mb-3 inline-block rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest"

          style={{ background: `${ACCENT}22`, color: ACCENT }}

        >

          {badge}

        </span>

      )}

      <h3 className="font-display text-xl font-bold sm:text-2xl">{title}</h3>

      <p className="mt-2 text-base sm:text-lg" style={{ color: 'var(--plugin-muted)' }}>

        {prompt}

      </p>

      <div className="mt-6">{children}</div>

    </div>

  );

}



function HeatmapOverlay({

  heatmap,

  maxCount,

  layout,

  zoom = 1,

  pan = { x: 0, y: 0 },

  showTarget,

  target,

}: {

  heatmap: PinpointHeatCell[];

  maxCount: number;

  layout: ImageLayout;

  zoom?: number;

  pan?: { x: number; y: number };

  showTarget?: boolean;

  target?: { x: number; y: number; radius: number };

}) {

  const cx = layout.left + layout.width / 2;

  const cy = layout.top + layout.height / 2;



  const mapX = (nx: number) => cx + (layout.left + nx * layout.width - cx) * zoom + pan.x;

  const mapY = (ny: number) => cy + (layout.top + ny * layout.height - cy) * zoom + pan.y;

  const mapW = (w: number) => w * layout.width * zoom;

  const mapH = (h: number) => h * layout.height * zoom;



  return (

    <>

      {heatmap.map((cell, i) => {

        const intensity = cell.count / Math.max(maxCount, 1);

        const size = 12 + intensity * 48;

        return (

          <div

            key={`${cell.x}-${cell.y}-${i}`}

            className="pointer-events-none absolute rounded-full"

            style={{

              left: mapX(cell.x),

              top: mapY(cell.y),

              width: size,

              height: size,

              transform: 'translate(-50%, -50%)',

              background: `radial-gradient(circle, rgba(239,68,68,${0.35 + intensity * 0.5}) 0%, rgba(249,115,22,${intensity * 0.3}) 50%, transparent 70%)`,

            }}

          />

        );

      })}

      {showTarget && target && (

        <div

          className="pointer-events-none absolute rounded-full border-2 border-dashed border-emerald-500"

          style={{

            left: mapX(target.x),

            top: mapY(target.y),

            width: mapW(target.radius * 2),

            height: mapH(target.radius * 2),

            transform: 'translate(-50%, -50%)',

            boxShadow: '0 0 0 4px rgba(34,197,94,0.15)',

          }}

        />

      )}

    </>

  );

}



function PinpointCanvas({

  imageUrl,

  heatmap,

  myTap,

  onTap,

  showTarget,

  target,

  fill = false,

  zoomable = false,

}: {

  imageUrl: string;

  heatmap: PinpointHeatCell[];

  myTap?: { x: number; y: number };

  onTap?: (x: number, y: number) => void;

  showTarget?: boolean;

  target?: { x: number; y: number; radius: number };

  fill?: boolean;

  zoomable?: boolean;

}) {

  const containerRef = useRef<HTMLDivElement>(null);

  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });

  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

  const [zoom, setZoom] = useState(1);

  const [pan, setPan] = useState({ x: 0, y: 0 });

  const gestureRef = useRef({

    mode: 'none' as 'none' | 'pan' | 'pinch',

    startPan: { x: 0, y: 0 },

    startPoint: { x: 0, y: 0 },

    pinchDist: 0,

    pinchZoom: 1,

    moved: false,

  });



  const layout = computeContainLayout(

    containerSize.w,

    containerSize.h,

    naturalSize.w,

    naturalSize.h,

  );



  useEffect(() => {

    const el = containerRef.current;

    if (!el) return;

    const ro = new ResizeObserver((entries) => {

      const entry = entries[0];

      if (!entry) return;

      setContainerSize({ w: entry.contentRect.width, h: entry.contentRect.height });

    });

    ro.observe(el);

    setContainerSize({ w: el.clientWidth, h: el.clientHeight });

    return () => ro.disconnect();

  }, []);



  const resetView = useCallback(() => {

    setZoom(1);

    setPan({ x: 0, y: 0 });

  }, []);



  const pointToNormalized = useCallback(

    (clientX: number, clientY: number) => {

      if (!containerRef.current || !layout.width || !layout.height) return null;

      const rect = containerRef.current.getBoundingClientRect();

      const px = clientX - rect.left;

      const py = clientY - rect.top;

      const cx = rect.width / 2;

      const cy = rect.height / 2;

      const ux = (px - cx - pan.x) / zoom + cx;

      const uy = (py - cy - pan.y) / zoom + cy;

      const x = (ux - layout.left) / layout.width;

      const y = (uy - layout.top) / layout.height;

      if (x < 0 || x > 1 || y < 0 || y > 1) return null;

      return { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) };

    },

    [layout.height, layout.left, layout.top, layout.width, pan.x, pan.y, zoom],

  );



  const handleTapAt = useCallback(

    (clientX: number, clientY: number) => {

      if (!onTap) return;

      const point = pointToNormalized(clientX, clientY);

      if (point) onTap(point.x, point.y);

    },

    [onTap, pointToNormalized],

  );



  const onTouchStart = useCallback(

    (e: React.TouchEvent) => {

      if (!zoomable) return;

      if (e.touches.length === 2) {

        const [a, b] = [e.touches[0], e.touches[1]];

        const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

        gestureRef.current = {

          ...gestureRef.current,

          mode: 'pinch',

          pinchDist: dist,

          pinchZoom: zoom,

          moved: true,

        };

      } else if (e.touches.length === 1 && zoom > 1) {

        const t = e.touches[0];

        gestureRef.current = {

          ...gestureRef.current,

          mode: 'pan',

          startPan: { ...pan },

          startPoint: { x: t.clientX, y: t.clientY },

          moved: false,

        };

      }

    },

    [pan, zoom, zoomable],

  );



  const onTouchMove = useCallback(

    (e: React.TouchEvent) => {

      if (!zoomable) return;

      const g = gestureRef.current;

      if (g.mode === 'pinch' && e.touches.length === 2) {

        e.preventDefault();

        const [a, b] = [e.touches[0], e.touches[1]];

        const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

        if (g.pinchDist > 0) {

          const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, g.pinchZoom * (dist / g.pinchDist)));

          setZoom(next);

        }

        g.moved = true;

      } else if (g.mode === 'pan' && e.touches.length === 1) {

        e.preventDefault();

        const t = e.touches[0];

        const dx = t.clientX - g.startPoint.x;

        const dy = t.clientY - g.startPoint.y;

        if (Math.hypot(dx, dy) > 8) g.moved = true;

        setPan({ x: g.startPan.x + dx, y: g.startPan.y + dy });

      }

    },

    [zoomable],

  );



  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const g = gestureRef.current;
      if (onTap && e.changedTouches[0]) {
        const t = e.changedTouches[0];
        const canTap =
          !zoomable || g.mode === 'none' || (g.mode === 'pan' && !g.moved);
        if (canTap && g.mode !== 'pinch') handleTapAt(t.clientX, t.clientY);
      }
      if (e.touches.length < 2) g.mode = 'none';
      if (e.touches.length === 0) {
        g.mode = 'none';
        g.moved = false;
      }
    },
    [handleTapAt, onTap, zoomable],
  );



  const maxCount = Math.max(1, ...heatmap.map((c) => c.count));

  const tapCount = heatmap.reduce((s, c) => s + c.count, 0);

  const cx = layout.left + layout.width / 2;

  const cy = layout.top + layout.height / 2;

  const imgStyle =

    naturalSize.w > 0

      ? {

          left: cx + (layout.left - cx) * zoom + pan.x,

          top: cy + (layout.top - cy) * zoom + pan.y,

          width: layout.width * zoom,

          height: layout.height * zoom,

        }

      : undefined;



  return (

    <div className="flex min-h-0 flex-col gap-2">

      {zoomable && (

        <div className="pinpoint-zoom-controls flex shrink-0 items-center justify-center gap-2">

          <button

            type="button"

            className="pinpoint-zoom-btn"

            aria-label="Alejar"

            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - 0.5).toFixed(2)))}

          >

            −

          </button>

          <span className="min-w-[3rem] text-center text-xs font-bold tabular-nums" style={{ color: 'var(--plugin-muted)' }}>

            {Math.round(zoom * 100)}%

          </span>

          <button

            type="button"

            className="pinpoint-zoom-btn"

            aria-label="Acercar"

            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + 0.5).toFixed(2)))}

          >

            +

          </button>

          <button type="button" className="pinpoint-zoom-btn pinpoint-zoom-btn--ghost" onClick={resetView}>

            Reset

          </button>

          <span className="text-[10px] font-medium" style={{ color: 'var(--plugin-muted)' }}>

            Pellizca o arrastra para afinar

          </span>

        </div>

      )}

      <div

        ref={containerRef}

        className={`pinpoint-canvas relative overflow-hidden rounded-xl border-2 ${onTap ? 'cursor-crosshair' : ''} ${fill ? 'h-full min-h-[min(52vh,420px)] w-full' : 'mx-auto w-full max-w-3xl'}`}

        style={{

          borderColor: 'var(--plugin-border)',

          background: 'var(--plugin-btn-bg, #f8fafc)',

          aspectRatio: fill ? undefined : '4/3',

          minHeight: fill ? undefined : 'min(280px, 50vh)',

          touchAction: zoomable ? 'none' : 'manipulation',

        }}

        onClick={

          onTap && !zoomable

            ? (e) => {

                e.preventDefault();

                handleTapAt(e.clientX, e.clientY);

              }

            : onTap && zoomable

              ? (e) => {

                  if (!gestureRef.current.moved) handleTapAt(e.clientX, e.clientY);

                }

              : undefined

        }

        onTouchStart={onTap ? onTouchStart : undefined}

        onTouchMove={onTap ? onTouchMove : undefined}

        onTouchEnd={onTap ? onTouchEnd : undefined}

        role={onTap ? 'button' : undefined}

      >

        {naturalSize.w > 0 && imgStyle && (

          <img

            src={imageUrl}

            alt=""

            draggable={false}

            className="pointer-events-none absolute select-none"

            style={{ ...imgStyle, objectFit: 'contain' }}

          />

        )}

        {!naturalSize.w && (

          <img

            src={imageUrl}

            alt=""

            className="hidden"

            onLoad={(e) => {

              const img = e.currentTarget;

              setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });

            }}

          />

        )}

        {naturalSize.w > 0 && (

          <HeatmapOverlay

            heatmap={heatmap}

            maxCount={maxCount}

            layout={layout}

            zoom={zoomable ? zoom : 1}

            pan={zoomable ? pan : { x: 0, y: 0 }}

            showTarget={showTarget}

            target={target}

          />

        )}

        {myTap && naturalSize.w > 0 && (

          <div

            className="pointer-events-none absolute h-4 w-4 rounded-full border-2 border-white shadow-lg"

            style={{

              left: cx + (layout.left + myTap.x * layout.width - cx) * (zoomable ? zoom : 1) + (zoomable ? pan.x : 0),

              top: cy + (layout.top + myTap.y * layout.height - cy) * (zoomable ? zoom : 1) + (zoomable ? pan.y : 0),

              transform: 'translate(-50%, -50%)',

              background: ACCENT,

            }}

          />

        )}

        <p className="pointer-events-none absolute bottom-2 right-2 rounded bg-black/50 px-2 py-1 text-[10px] font-bold text-white">

          {tapCount} clics

        </p>

      </div>

    </div>

  );

}



export function PinpointHostView(props: ActivityViewProps<'point'>) {

  const { payload, runtime, phase } = props;

  const showTarget = phase === 'RESULTS';

  return (

    <Shell badge="Mapa de calor" title={payload.title} prompt={payload.prompt} fill>

      <PinpointCanvas

        imageUrl={payload.imageUrl}

        heatmap={runtime.heatmap}

        showTarget={showTarget}

        target={payload.target}

        fill

      />

      <p

        className="mt-2 shrink-0 text-center text-xs font-bold uppercase tracking-widest"

        style={{ color: 'var(--plugin-muted)' }}

      >

        {runtime.totalTaps} estudiante{runtime.totalTaps !== 1 ? 's' : ''} marcaron un punto

      </p>

    </Shell>

  );

}



export function PinpointStudentView(props: ActivityViewProps<'point'>) {

  const { payload, runtime, participantId, phase, dispatch } = props;

  const waiting = phase === 'PREPARING' || phase === 'LOBBY';

  const myTap = participantId ? runtime.taps[participantId] : undefined;

  const done = Boolean(myTap);



  if (waiting) {

    return (

      <Shell badge={GAME_MODE_CATALOG.point.displayName} title={payload.title} prompt={payload.prompt}>

        <p

          className="rounded-xl px-4 py-6 text-center"

          style={{ background: 'var(--plugin-btn-bg)', color: 'var(--plugin-muted)' }}

        >

          Espera a que el profesor pulse <strong>Comenzar</strong>

        </p>

      </Shell>

    );

  }



  return (

    <Shell badge={done ? 'Punto enviado' : 'Toca la imagen'} title={payload.title} prompt={payload.prompt}>

      <PinpointCanvas

        imageUrl={payload.imageUrl}

        heatmap={[]}

        myTap={myTap}

        zoomable={!done && phase === 'ACTIVE'}

        onTap={done || phase !== 'ACTIVE' ? undefined : (x, y) => dispatch({ type: 'TAP', payload: { x, y } })}

      />

      {done && (

        <p className="mt-4 text-center text-sm" style={{ color: 'var(--plugin-muted)' }}>

          Tu marca está registrada. Esperando al resto…

        </p>

      )}

    </Shell>

  );

}



export function PinpointProjectorView(props: ActivityViewProps<'point'>) {

  const { payload, runtime, phase } = props;

  return (

    <Shell badge="Calor en vivo" title={payload.title} prompt={payload.prompt} fill>

      <PinpointCanvas

        imageUrl={payload.imageUrl}

        heatmap={runtime.heatmap}

        showTarget={phase === 'RESULTS'}

        target={payload.target}

        fill

      />

    </Shell>

  );

}


